import React, { forwardRef, Fragment, useImperativeHandle } from 'react';
import { useFormik, FormikValues } from 'formik';
import { path } from 'ramda';
import { MasksConfig, MaskTemplate, LanguageInfo } from 'types';
import { Validators } from 'core';
import styled from 'styled-components';
import { Checkbox, Input as AntdInput, Col } from 'antd';

import { useIntl } from 'common/config/i18n';
import { StyledRepositoryForm, StyledRepositoryFormButtonWrapper } from 'common/styling/mixins';
import FormError from 'components/molecules/form-error';
import Button from 'components/molecules/button';
import Input from 'components/organisms/input';
import UploadField from 'components/organisms/upload-field';
import { KeywordsFormProvider } from 'components/organisms/input-keywords/keywords-context';

interface BaseFormValues {
  mailDefaultObject?: string;
  defaultHeadband?: string;
  mailEnabledByDefault?: boolean;
  smsEnabledByDefault?: boolean;
  flashEnabledByDefault?: boolean;
  whatsAppEnabledByDefault?: boolean;
  mailFile?: File;
  mailFileNameSaved?: string;
  smsFile?: File;
  smsFileNameSaved?: string;
  whatsappRefFile?: File;
  whatsappRefFileNameSaved?: string;
  whatsappMaskFile?: File;
  whatsappMaskFileNameSaved?: string;
}

type TranslationFields = {
  [K in `${string}-mailFile`]?: File;
} & {
  [K in `${string}-mailFileNameSaved`]?: string;
} & {
  [K in `${string}-mailDefaultObject`]?: string;
};

export type FormValues = BaseFormValues & TranslationFields;

interface Props {
  onSubmit: (values: FormikValues) => void;
  maskTemplate?: MaskTemplate;
  translationLanguages: LanguageInfo[];
  masksConfig: MasksConfig;
  status?: 'error' | 'loading' | 'success';
}

const { TextArea } = AntdInput;

const TextAreaWrapper = styled.div`
  margin-bottom: 0.625rem;
`;
const UpLabel = styled.div`
  font-size: ${path(['theme', 'font', 'size', 'body'])};
  font-weight: bold;
`;
const SectionWrapper = styled.div`
  margin-left: 0.625rem;
  margin-bottom: 1.25rem;
  display: flex;
  flex-direction: column;
`;
const SectionTitle = styled.p`
  font-size: ${path(['theme', 'font', 'size', 'h3'])};
  font-weight: bold;
`;
const CheckboxLabel = styled.span`
  font-size: ${path(['theme', 'font', 'size', 'body'])};
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Form = forwardRef((props: Props, ref: any) => {
  const { t } = useIntl();

  const getInitialValues = (): FormValues => {
    const { maskTemplate, translationLanguages } = props;
    const mailConfig = maskTemplate?.mailConfiguration;
    const smsConfig = maskTemplate?.smsConfiguration;
    const whatsappConfig = maskTemplate?.whatsappConfiguration;

    // Create mailConfig translation specific fields
    const translationFields = translationLanguages.reduce((acc, lang) => {
      const savedFileName = mailConfig?.translations?.find((t) => t.languageCode === lang.code)?.fileName || '';
      const mailDefaultObject =
        mailConfig?.translations?.find((t) => t.languageCode === lang.code)?.defaultObject || '';

      return {
        ...acc,
        [`${lang.code}-mailFile`]: undefined,
        [`${lang.code}-mailFileNameSaved`]: savedFileName ?? undefined,
        [`${lang.code}-mailDefaultObject`]: mailDefaultObject ?? undefined,
      };
    }, {} as Partial<FormValues>);

    return {
      mailDefaultObject: mailConfig?.defaultObject ?? undefined,
      defaultHeadband: mailConfig?.defaultHeadband ?? undefined,
      mailEnabledByDefault: mailConfig?.enabledByDefault ?? undefined,
      smsEnabledByDefault: smsConfig?.enabledByDefault ?? undefined,
      flashEnabledByDefault: maskTemplate?.flashEnabled ?? undefined,
      whatsAppEnabledByDefault: whatsappConfig?.enabledByDefault ?? undefined,

      mailFile: undefined,
      mailFileNameSaved: mailConfig?.fileName ?? undefined,
      smsFile: undefined,
      smsFileNameSaved: smsConfig?.fileName ?? undefined,
      whatsappRefFile: undefined,
      whatsappRefFileNameSaved: whatsappConfig?.refFileName ?? undefined,
      whatsappMaskFile: undefined,
      whatsappMaskFileNameSaved: whatsappConfig?.maskFileName ?? undefined,

      // Translation fields
      ...translationFields,
    } as FormValues;
  };

  const formik = useFormik({
    initialValues: getInitialValues(),
    validate: (values) => Validators.formMask(values, props.translationLanguages),
    onSubmit: (values) => {
      props.onSubmit(values);
    },
  });

  useImperativeHandle(ref, () => ({
    resetForm() {
      formik.resetForm();
    },
  }));

  const MailUploadWithTranslations = () => {
    const deleteMailFiles = () => {
      const fieldsToReset = {
        mailFile: undefined,
        mailFileNameSaved: undefined,
        ...Object.fromEntries(
          props.translationLanguages.flatMap((lang) => [
            [`${lang.code}-mailFile`, undefined],
            [`${lang.code}-mailFileNameSaved`, undefined],
          ])
        ),
      };
      formik.setValues((prev) => ({ ...prev, ...fieldsToReset }));
    };

    return (
      <>
        <UploadField
          label={t('table', 'CHART_MASK')}
          subLabel={t('basicLanguagaeLabel')}
          name="mailFile"
          value={formik?.values?.mailFile?.name}
          savedValue={formik?.values?.mailFileNameSaved}
          formik={formik}
          fileType=".cshtml"
          deleteFile={deleteMailFiles}
        />
        {
          // Add mailConfig translation specific inputs
          props.translationLanguages.map((lang) => (
            <UploadField
              key={lang.code}
              subLabel={lang.label}
              name={`${lang.code}-mailFile`}
              value={formik?.values?.[`${lang.code}-mailFile`]?.name}
              savedValue={formik?.values?.[`${lang.code}-mailFileNameSaved`]}
              formik={formik}
              fileType=".cshtml"
            />
          ))
        }
      </>
    );
  };
  const SmsUpload = () => {
    const deleteSmsFiles = () => {
      formik.setFieldValue('smsFile', undefined);
      formik.setFieldValue('smsFileNameSaved', undefined);
    };

    return (
      <UploadField
        label={t('table', 'CHART_MASK')}
        name="smsFile"
        value={formik?.values?.smsFile?.name}
        savedValue={formik?.values?.smsFileNameSaved}
        formik={formik}
        fileType=".cshtml"
        deleteFile={deleteSmsFiles}
      />
    );
  };
  const WhatsappUpload = () => {
    const deleteWhatsappRefFiles = () => {
      formik.setFieldValue('whatsappRefFile', undefined);
      formik.setFieldValue('whatsappRefFileNameSaved', undefined);
    };
    const deleteWhatsappMaskFiles = () => {
      formik.setFieldValue('whatsappMaskFile', undefined);
      formik.setFieldValue('whatsappMaskFileNameSaved', undefined);
    };

    return (
      <>
        <UploadField
          label={t('table', 'CHART_MASK_REFERENCES')}
          name="whatsappRefFile"
          value={formik?.values?.whatsappRefFile?.name}
          savedValue={formik?.values?.whatsappRefFileNameSaved}
          formik={formik}
          fileType=".json"
          deleteFile={deleteWhatsappRefFiles}
        />
        <UploadField
          label={t('table', 'CHART_MASK_PREVIEW')}
          name="whatsappMaskFile"
          value={formik?.values?.whatsappMaskFile?.name}
          savedValue={formik?.values?.whatsappMaskFileNameSaved}
          formik={formik}
          fileType=".cshtml"
          deleteFile={deleteWhatsappMaskFiles}
        />
      </>
    );
  };

  return (
    <KeywordsFormProvider>
      <StyledRepositoryForm onSubmit={formik.handleSubmit}>
        <SectionTitle>{t('table', 'CHART_MAIL_CHANNEL')}</SectionTitle>
        <SectionWrapper>
          <>
            <UpLabel>{t('table', 'CHART_DEFAULT_OBJECT')}</UpLabel>
            <Col style={{ marginLeft: 20 }}>
              <Input
                subLabel={t('basicLanguagaeLabel')}
                name="mailDefaultObject"
                onChange={(text) => formik.handleChange({ target: { name: 'mailDefaultObject', value: text } })}
                value={formik.values.mailDefaultObject}
                width="100%"
                wrapperStyle={{ marginBottom: '0.625rem' }}
                withKeywords
                autoSize
              />
              <FormError formik={formik} fieldName="mailDefaultObject" />

              {
                // Add mailDefaultObject translation specific inputs
                props.translationLanguages.map((lang) => (
                  <Fragment key={lang.code}>
                    <Input
                      subLabel={lang.label}
                      name={`${lang.code}-mailDefaultObject`}
                      value={formik?.values?.[`${lang.code}-mailDefaultObject`]}
                      onChange={(text) =>
                        formik.handleChange({ target: { name: `${lang.code}-mailDefaultObject`, value: text } })
                      }
                      width="100%"
                      wrapperStyle={{ marginBottom: '0.625rem' }}
                      withKeywords
                      autoSize
                    />
                    <FormError formik={formik} fieldName={`${lang.code}-mailDefaultObject`} />
                  </Fragment>
                ))
              }
            </Col>
          </>

          <TextAreaWrapper>
            <UpLabel>{t('table', 'CHART_DEFAULT_HEADBAND')}</UpLabel>
            <TextArea
              name="defaultHeadband"
              onChange={formik.handleChange}
              value={formik.values.defaultHeadband}
              autoSize
            />
          </TextAreaWrapper>
          <FormError formik={formik} fieldName="defaultHeadband" />
          <Checkbox
            name="mailEnabledByDefault"
            checked={formik.values.mailEnabledByDefault}
            defaultChecked={formik.values.mailEnabledByDefault}
            onChange={(e) => formik.setFieldValue('mailEnabledByDefault', e.target.checked)}
          >
            <CheckboxLabel>{t('table', 'CHART_CHANNEL_CHECKED')}</CheckboxLabel>
          </Checkbox>
          <MailUploadWithTranslations />
        </SectionWrapper>

        {props.masksConfig?.sms && (
          <>
            <SectionTitle>{t('table', 'CHART_SMS_CHANNEL')}</SectionTitle>
            <SectionWrapper>
              <Checkbox
                name="smsEnabledByDefault"
                checked={formik.values.smsEnabledByDefault}
                defaultChecked={formik.values.smsEnabledByDefault}
                onChange={(e) => formik.setFieldValue('smsEnabledByDefault', e.target.checked)}
              >
                <CheckboxLabel>{t('table', 'CHART_CHANNEL_CHECKED')}</CheckboxLabel>
              </Checkbox>
              <SmsUpload />
            </SectionWrapper>
          </>
        )}

        {props.masksConfig?.flashInfo && (
          <>
            <SectionTitle>{t('table', 'CHART_FLASH_INFO')}</SectionTitle>

            <SectionWrapper>
              <Checkbox
                name="flashEnabledByDefault"
                checked={formik.values.flashEnabledByDefault}
                defaultChecked={formik.values.flashEnabledByDefault}
                onChange={(e) => formik.setFieldValue('flashEnabledByDefault', e.target.checked)}
              >
                <CheckboxLabel>{t('table', 'CHART_CHANNEL_CHECKED')}</CheckboxLabel>
              </Checkbox>
            </SectionWrapper>
          </>
        )}
        {props.masksConfig?.whatsApp && (
          <>
            <SectionTitle>{t('table', 'CHART_WHATSAPP_CHANNEL')}</SectionTitle>

            <SectionWrapper>
              <Checkbox
                name="whatsAppEnabledByDefault"
                checked={formik.values.whatsAppEnabledByDefault}
                defaultChecked={formik.values.whatsAppEnabledByDefault}
                onChange={(e) => formik.setFieldValue('whatsAppEnabledByDefault', e.target.checked)}
              >
                <CheckboxLabel>{t('table', 'CHART_CHANNEL_CHECKED')}</CheckboxLabel>
              </Checkbox>
              <WhatsappUpload />
            </SectionWrapper>
          </>
        )}
        <StyledRepositoryFormButtonWrapper>
          <Button
            height="35px"
            width="100%"
            borderRadius="20px"
            disabled={!!(formik.errors.mailDefaultObject || formik.errors.defaultHeadband)}
            htmlType="submit"
            loading={status === 'loading'}
          >
            {t('Buttons', 'SAVE_BUTTON')}
          </Button>
        </StyledRepositoryFormButtonWrapper>
      </StyledRepositoryForm>
    </KeywordsFormProvider>
  );
});

Form.displayName = 'Form';

export default Form;
